import { Box } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PageLoader from "../components/Loader";
import { useToast } from "../components/Toast";
import apiService from "../Services/api.service";
import { UI, btnSx } from "../components/ActionDrawer";
import DashTable, {
  FilterChips,
  KV,
  Oid,
  PageHead,
  RowCard,
  SearchBox,
  Tag,
  Toolbar,
} from "../components/DashTable";

const rupee = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const formatDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
      })
    : "Not sent";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "generated", label: "Generated" },
  { key: "sent_no_response", label: "Sent, no response" },
  { key: "accepted", label: "Accepted" },
];

// Derives a display status from the two underlying booleans (sent, signed).
const getStatus = (row) => {
  if (row.accepted) return "accepted";
  if (row.sent) return "sent_no_response";
  return "generated";
};

const STATUS_META = {
  generated: { label: "Generated", color: "default", tone: "i" },
  sent_no_response: {
    label: "Sent, no response",
    color: "warning",
    tone: "s",
  },
  accepted: { label: "Accepted", color: "success", tone: "g" },
};

const SyncTag = ({ synced }) => (
  <Tag tone={synced ? "bl" : "i"}>{synced ? "Synced" : "Not synced"}</Tag>
);

const PAGE_SIZE = 15;

const Quotations = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const token = localStorage.getItem("token");

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [activeFilter, setActiveFilter] = useState("all");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  const fetchQuotations = async (targetPage = page) => {
    setIsLoading(true);
    try {
      const res = await apiService.fetchQuotations(token, {
        page: targetPage,
        limit: PAGE_SIZE,
        status: activeFilter === "all" ? undefined : activeFilter,
        search: search || undefined,
      });
      setRows(res.data || []);
      setSummary(res.summary || null);
      setPagination(res.pagination || null);
    } catch (error) {
      toast.error("Failed to load quotations");
    } finally {
      setIsLoading(false);
    }
  };

  // Typing resets to page 1 and debounces the actual search request so each
  // keystroke doesn't fire its own network call.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [activeFilter]);

  useEffect(() => {
    fetchQuotations(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, activeFilter, search]);

  // Filtering/search now happen server-side (see api.service.js
  // fetchQuotations) — `rows` already reflects the current filter/search,
  // scoped correctly across the whole dataset, not just this page.
  const filteredRows = rows;

  const kpis = [
    { label: "Quotations", value: summary?.total ?? 0 },
    { label: "Generated", value: summary?.generated ?? 0 },
    { label: "Sent, no response", value: summary?.sentNoResponse ?? 0 },
    { label: "Accepted", value: summary?.accepted ?? 0 },
  ];

  const columns = [
    { label: "Quotation" },
    { label: "Order" },
    { label: "Clinic" },
    { label: "Value", num: true },
    { label: "Sent" },
    { label: "Status" },
    { label: "Books" },
  ];

  const tableRows = filteredRows.map((row) => {
    const status = getStatus(row);
    return {
      id: row.orderId,
      cells: [
        <Box component="span" sx={{ color: UI.p, fontWeight: 500 }}>
          {row.quotationNumber || "—"}
        </Box>,
        <Oid>{row.orderId?.slice(-8) || "—"}</Oid>,
        row.clinicName || "—",
        rupee(row.quoteTotal),
        <Box component="span" sx={{ color: UI.ink3 }}>
          {formatDate(row.sentAt)}
        </Box>,
        <Tag tone={STATUS_META[status].tone}>{STATUS_META[status].label}</Tag>,
        <SyncTag synced={row.zohoSynced} />,
      ],
    };
  });

  return (
    <Box
      sx={{
        padding: { xs: "18px 16px 50px", md: "24px 28px 60px" },
        maxWidth: 1240,
      }}
    >
      <PageHead
        title="Quotations"
        lede="One quotation per order. A revision creates a new number and supersedes the last."
        kpis={kpis}
      />

      <Toolbar
        right={
          <SearchBox
            value={searchInput}
            placeholder="Search order, clinic or doctor"
            onChange={(e) => setSearchInput(e.target.value)}
          />
        }
      >
        <FilterChips
          options={FILTERS.map((f) => ({ value: f.key, label: f.label }))}
          value={activeFilter}
          onChange={setActiveFilter}
        />
      </Toolbar>

      {/* MOBILE: stacked cards */}
      <Box
        sx={{
          display: { xs: "flex", sm: "none" },
          flexDirection: "column",
          gap: "10px",
          marginBottom: "28px",
        }}
      >
        {filteredRows.length === 0 ? (
          <Box
            sx={{
              padding: "34px 18px",
              textAlign: "center",
              fontSize: 13,
              color: UI.ink3,
              bgcolor: UI.surface,
              border: "1px solid",
              borderColor: UI.line,
              borderRadius: "8px",
            }}
          >
            No quotations match this filter.
          </Box>
        ) : (
          filteredRows.map((row) => {
            const status = getStatus(row);
            return (
              <RowCard
                key={row.orderId}
                onClick={() => navigate(`/dashboard/quotations/${row.orderId}`)}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: "8px",
                    marginBottom: "6px",
                  }}
                >
                  <Box sx={{ fontSize: 13, fontWeight: 500, color: UI.ink }}>
                    {row.clinicName || "—"}
                  </Box>
                  <Tag tone={STATUS_META[status].tone}>
                    {STATUS_META[status].label}
                  </Tag>
                </Box>

                <Box sx={{ marginBottom: "8px" }}>
                  <Oid>{row.quotationNumber || "—"}</Oid>
                </Box>

                <KV
                  label="Value"
                  value={
                    <Box component="span" sx={{ fontWeight: 500 }}>
                      {rupee(row.quoteTotal)}
                    </Box>
                  }
                />
                <KV label="Sent" value={formatDate(row.sentAt)} />
                <KV
                  label="Books"
                  value={<SyncTag synced={row.zohoSynced} />}
                />
              </RowCard>
            );
          })
        )}
      </Box>

      {/* DESKTOP: table */}
      <Box sx={{ display: { xs: "none", sm: "block" } }}>
        <DashTable
          columns={columns}
          rows={tableRows}
          onRowClick={(id) => navigate(`/dashboard/quotations/${id}`)}
          emptyText="No quotations match this filter."
          foot={`${filteredRows.length} shown · ${pagination?.total ?? rows.length} total`}
        />
      </Box>

      {pagination && pagination.total > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "10px",
            marginTop: "10px",
            fontSize: 13,
            color: UI.ink3,
          }}
        >
          <span>
            {(pagination.page - 1) * pagination.limit + 1}–
            {Math.min(pagination.page * pagination.limit, pagination.total)} of{" "}
            {pagination.total}
          </span>
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span>
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <Box sx={{ display: "flex", gap: "8px" }}>
              <Box
                component="button"
                type="button"
                onClick={() => setPage((p) => Math.max(p - 1, 1))}
                disabled={pagination.page <= 1}
                sx={btnSx("ghost", pagination.page <= 1)}
              >
                Previous
              </Box>
              <Box
                component="button"
                type="button"
                onClick={() => setPage((p) => Math.min(p + 1, pagination.totalPages))}
                disabled={pagination.page >= pagination.totalPages}
                sx={btnSx("ghost", pagination.page >= pagination.totalPages)}
              >
                Next
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {isLoading && <PageLoader />}
    </Box>
  );
};

export default Quotations;
